import { getSession } from 'next-auth/react'
import { REGION_HEADER, currentRegion } from '@/lib/region'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

export class DownloadError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'DownloadError'
    this.status = status
  }
}

/** Resolve a backend-relative download path (`/api/...`) to an absolute URL. */
export function resolveApiUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `${API_URL}${BASE_PATH}${url.startsWith('/') ? url : `/${url}`}`
}

/**
 * Fetch a protected file with the session bearer token and hand it to the
 * browser as a download. `apiClient` always parses JSON, so this goes through
 * raw `fetch` (same pattern as ReviewResolveTab's PDF preview).
 */
export async function downloadAuthenticatedFile(url: string, filename: string): Promise<void> {
  const session = await getSession()
  const headers: Record<string, string> = {}
  if (session?.accessToken) headers['Authorization'] = `Bearer ${session.accessToken}`
  headers[REGION_HEADER] = currentRegion()

  const response = await fetch(resolveApiUrl(url), { headers })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (body?.detail) detail = String(body.detail)
    } catch {
      // non-JSON error body — keep statusText
    }
    throw new DownloadError(response.status, detail)
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
